import React, { useState } from 'react';
import { Box, Button, Typography, styled, InputLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import * as MuiIcons from '@mui/icons-material';

import IconSearchComponent from './IconSearchComponent';

const IconPreview = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.background.paper,
  minHeight: 40,
  position: 'relative',
  '&:hover': {
    borderColor: theme.palette.text.primary,
  },
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    borderWidth: '2px',
    padding: 'calc(8px - 1px)', // Adjust padding when border increases
  },
}));

const IconDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.action.hover,
  flexShrink: 0,
}));

interface IconSelectorProps {
  value: string;
  onChange: (iconName: string) => void;
  label?: string;
  disabled?: boolean;
}

const IconSelector: React.FC<IconSelectorProps> = ({
  value,
  onChange,
  label = 'Icon',
  disabled = false,
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Create styled icon component for dynamic icons
  const createStyledIcon = (IconComponent: React.ComponentType) =>
    styled(IconComponent)(() => ({
      fontSize: 16,
      color: 'text.primary',
    }));

  const IconComponent = (MuiIcons as any)[value];
  const hasValidIcon = true; // IconComponent && typeof IconComponent === 'function';

  const handleOpenSearch = () => {
    if (!disabled) {
      setIsSearchOpen(true);
    }
  };

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
  };

  const handleSelectIcon = (iconName: string) => {
    onChange(iconName);
    setIsSearchOpen(false);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {label && (
        <InputLabel
          shrink
          sx={{
            position: 'absolute',
            top: -8,
            left: 8,
            backgroundColor: 'background.paper',
            px: 0.5,
            fontSize: '0.75rem',
            zIndex: 1,
            color: 'text.secondary',
          }}
        >
          {label}
        </InputLabel>
      )}

      <IconPreview>
        <IconDisplay>
          {hasValidIcon && IconComponent ? (
            (() => {
              const DynamicStyledIcon = createStyledIcon(IconComponent);
              return <DynamicStyledIcon />;
            })()
          ) : (
            <Typography variant="body2" color="text.secondary">
              ?
            </Typography>
          )}
        </IconDisplay>

        <Box flex={1}>
          <Typography variant="body2" fontWeight={500}>
            {value || 'No icon selected'}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          onClick={handleOpenSearch}
          disabled={disabled}
          size="small"
          sx={{ minWidth: 40, px: 1 }}
        >
          <SearchIcon fontSize="small" />
        </Button>
      </IconPreview>

      <IconSearchComponent
        open={isSearchOpen}
        onClose={handleCloseSearch}
        onSelectIcon={handleSelectIcon}
        currentIcon={value}
      />
    </Box>
  );
};

export default IconSelector;
